import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';

import { useGraphRef } from '../hooks/useGraphRef';
import { subgraphsRouteConfig } from '../routes';
import { NonPublicGraphArea } from '../variantLayout/NonPublicGraphArea';
import { ProtectedGraphArea } from '../variantLayout/ProtectedGraphArea';

import { subgraphsConfigRouteConfig } from './routes';

const SubgraphsConfigPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "SubgraphsConfigurationPage" */ './subgraphsConfigurationPage/SubgraphsConfigurationPage'
    )
  ).SubgraphsConfigurationPage,
}));

const SubgraphsPage = React.lazy(async () => ({
  default: (
    await import(
      /* webpackPrefetch: true, webpackChunkName: "SubgraphsPage" */ './subgraphsPage/SubgraphsPage'
    )
  ).SubgraphsPage,
}));

export const SubgraphsLayout = ({
  isCloudSupergraph,
}: {
  isCloudSupergraph: boolean;
}) => {
  const [currentAccountId] = useCurrentAccountId();
  const graphRef = useGraphRef();

  const currentPlanResult = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlanResult?.currentPlan?.tier,
    kind: currentPlanResult?.currentPlan?.kind,
  });

  if (!currentAccountId || !graphRef) return <Loading />;

  return (
    <React.Suspense fallback={<Loading />}>
      <Switch>
        <Route
          path={subgraphsRouteConfig.definition}
          render={() => (
            <NonPublicGraphArea>
              <ProtectedGraphArea accountId={currentAccountId}>
                <DocumentTitle title="Subgraphs" />
                <PermissionGuard
                  graphId={graphRef?.graphId}
                  graphPermissions={['canQueryImplementingServices']}
                  fallback={PermissionBlockCard}
                >
                  <SubgraphsPage
                    isCloudSupergraph={isCloudSupergraph}
                    accountId={currentAccountId}
                  />
                </PermissionGuard>
              </ProtectedGraphArea>
            </NonPublicGraphArea>
          )}
          exact
        />
        {isDedicatedPlan && (
          <Route
            path={subgraphsConfigRouteConfig.definition}
            render={() => (
              <NonPublicGraphArea>
                <ProtectedGraphArea accountId={currentAccountId}>
                  <DocumentTitle title="Subgraph Configuration" />
                  <PermissionGuard
                    graphId={graphRef?.graphId}
                    graphPermissions={[
                      'canQueryImplementingServices',
                      'canManageVariants',
                    ]}
                    fallback={PermissionBlockCard}
                  >
                    <SubgraphsConfigPage
                      graphRef={graphRef}
                      currentAccountId={currentAccountId}
                    />
                  </PermissionGuard>
                </ProtectedGraphArea>
              </NonPublicGraphArea>
            )}
            exact
          />
        )}
      </Switch>
    </React.Suspense>
  );
};
